import { slugify } from "@/lib/slugify";
import { getLocalizedField } from "@/lib/localized-field";

/**
 * ING / externally-sold voucher products are a partner-channel set that
 * is deliberately kept out of the homepage, category pages and the
 * /vouchers index. They have NO working `/vouchers/<slug>` detail page.
 * The canonical voucher resolver `/deals/package-as-voucher` does not
 * return them, so a `voucherUrl()` link 404s (see the long note in
 * `packages/[category]/page.js`). Their only working detail page is the
 * deal-detail route `/packages/<category>/<slug>?q=<id>`.
 *
 * Detection mirrors the inline predicate that has lived in the deal
 * detail page (`isIngVoucher`) since the ING launch. On live data the
 * `is_externally_sold` flag is effectively dead (same note), so in
 * practice the title-prefix branch is what actually fires, but we keep
 * the flag checks so the helper stays correct if the backend ever starts
 * emitting it. This module is the single source of truth shared by the
 * /ing hub page and the detail-page breadcrumb so the two never drift.
 *
 * NB hub sourcing (2026-08-27): the /ing hub no longer derives its deal
 * LIST from the generic public feed + these predicates. Since 2026-08-17
 * every ING deal permanently carries the `unlisted` marker (hard rule:
 * ING products never between normal deals on the site), so that feed
 * rightly stopped returning them and the hub rendered permanently empty.
 * The list now comes from the dedicated price-free BFF surface
 * `GET /deals/ing-hub` (selected backend-side from current externally sold
 * VoucherCampaign rows) via `fetchIngHubDeals()` in src/services/deals.js.
 * The helpers here still drive the detail-page ING treatment
 * (breadcrumb, routing) and the hub-card hrefs.
 */

export const ING_HUB_PATH = "/ing";
export const ING_DETAIL_QUERY = "ing=1";

// Exact predicate preserved from the previous inline check so the
// detail page keeps byte-identical behaviour: "ING" at the start
// followed by a space or dash. "Ingrid", "Ingang" etc. never match
// because the char after "ing" must be a separator.
const ING_TITLE_RE = /^ing[\s\-–—]/i;

function resolveTitle(deal, locale = "nl") {
  if (!deal) return "";
  const t = getLocalizedField(deal, "title", locale) || deal.title || "";
  return typeof t === "string" ? t : "";
}

/**
 * True when a deal is an ING / externally-sold voucher product.
 *
 * @param deal               the deal payload
 * @param opts.title         pre-resolved title string (skips locale lookup);
 *                           pass the already-localised title from a caller
 *                           that has one to stay behaviour-identical.
 * @param opts.locale        locale used to resolve the title when `title`
 *                           is not supplied (default "nl").
 */
export function isIngDeal(deal, { title, locale = "nl" } = {}) {
  if (!deal) return false;
  if (deal.is_externally_sold === true || deal.isExternallySold === true) return true;
  const resolved = typeof title === "string" ? title : resolveTitle(deal, locale);
  return ING_TITLE_RE.test(resolved);
}

/**
 * Build the query string for a deal information page while preserving the ING
 * cache-classification marker on every self URL. This is shared by hub links,
 * metadata canonicals/hreflang and Product JSON-LD so an externally-sold deal
 * without an `ING` title prefix cannot fall back into the long shared cache via
 * an unmarked canonical URL.
 */
export function ingAwareDealDetailSearch(
  deal,
  id,
  { title, locale = "nl", roundtrip = false } = {},
) {
  if (roundtrip || id == null) return "";
  const search = `?q=${encodeURIComponent(String(id))}`;
  return isIngDeal(deal, { title, locale })
    ? `${search}&${ING_DETAIL_QUERY}`
    : search;
}

/**
 * True when a deal carries the "unlisted" marker in `category_ids`: hidden
 * from every generic public listing surface while its direct detail URL
 * keeps working (originally the draft mechanism, Dennis 2026-06-04: the ING
 * Italy draft "alleen mensen die de url weten zien hem"; since 2026-08-17 a
 * hard rule for EVERY ING deal). NB: since 2026-08-27 this marker no longer
 * decides /ing hub membership; the hub's dedicated backend surface follows
 * the active externally sold campaign instead (see the module header). Kept as
 * the frontend-side reader for the marker (and cited as the category_ids
 * parsing precedent in src/lib/roundtrip.js). The detail page's ING
 * treatment (ING breadcrumb etc.) is driven by `isIngDeal` and is
 * intentionally NOT affected by this marker.
 */
export function isUnlistedDeal(deal) {
  if (!deal) return false;
  let cats = deal.category_ids;
  if (typeof cats === "string") {
    try {
      cats = JSON.parse(cats);
    } catch {
      cats = [];
    }
  }
  return (
    Array.isArray(cats) &&
    cats.some((c) => String(c).trim().toLowerCase() === "unlisted")
  );
}

/**
 * Strip the leading "ING - " / "ING " / "ING: " brand prefix from a
 * product title for display (e.g. in the breadcrumb crumb that already
 * sits under a clickable "ING" hub crumb, so the brand isn't repeated).
 * Falls back to the original title if stripping would leave it empty.
 */
export function stripIngPrefix(title) {
  if (typeof title !== "string") return title;
  const stripped = title.replace(/^ing[\s\-–—:]+/i, "").trim();
  return stripped || title;
}

/**
 * Build the working detail-page URL for an ING product. ING products are
 * voucher-flagged but must link to the deal route, never `/vouchers/...`
 * (which 404s for them). The detail page resolves the deal by the `q`
 * id regardless of the category/slug segments, but we still emit the
 * deal's own category + a `slugify(title)` slug so the URL matches the
 * page's own canonical. `ing=1` is a non-secret cache-classification marker
 * for externally-sold rows whose title has no ING prefix. Middleware also
 * recognizes the legacy `ing-...` flyer/canonical slugs, keeping every known
 * campaign-dependent information route out of the shared edge cache so a 404
 * visited before activation cannot stay pinned after the offer appears.
 */
export function ingDealHref(deal) {
  const id = deal?.deals_id ?? deal?.id;
  const cat = encodeURIComponent(deal?.category_id || deal?.category_name || "deals");
  const slug = deal?.title ? slugify(deal.title) : String(id ?? "");
  return id != null
    ? `/packages/${cat}/${slug}${ingAwareDealDetailSearch(deal, id)}`
    : `/packages/${cat}/${slug}`;
}
